import { addError, addLoading, removeLoadingAndError } from '../core/redux/api/module';
import { updateBreaker } from '../core/redux/api/circuitBreaker/module';
import { updateResortMapping} from '../core/redux/api/endpoints';
import { IAppState } from '../core/redux/IAppState';
import {
  ApiReducer,
  makeApiAction,
  makeApiReducer,
  RsaaTypes,
} from '../core/redux/api/reduxStandardApiAction';
import { ACTION_TYPE } from '../core/redux/actionType';
import {ResortMappingObject, RTPMappingProduct, SiriuswareMappingProduct} from './productMappings';
import { getResource } from '../language/resources/getResource';
import { assoc } from '../language/module';



const KEY_ACTION = ACTION_TYPE.UPDATE_RESORT_MAPPING_REQUEST;
const ACTIONS    = [
  ACTION_TYPE.UPDATE_RESORT_MAPPING_REQUEST,
  ACTION_TYPE.UPDATE_RESORT_MAPPING_RESPONSE,
  ACTION_TYPE.UPDATE_RESORT_MAPPING_ERROR,
];


/**
 * Makes an action that can be dispatched to get a Resorts Entitlements for the Resort Entitlement Mapping Tool.
 */
export const UpdateResortMappingsAction = (
  updatedMappingsArray: RTPMappingProduct[] | SiriuswareMappingProduct[],
) => makeApiAction(
  {
    method:   'PUT',
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    endpoint: state => updateResortMapping(state.selectedResort.resortAbbreviation),
    body: JSON.stringify(updatedMappingsArray),
    types:    ACTIONS.map(action => ({ type: action, meta: { updatedMappingsArray } })) as RsaaTypes,
    bailout:  (state: IAppState) => false
  },
);


const reducer: ApiReducer<ResortMappingObject> = (
  state,
  action,
  error,
  isLoading,
  payload
): IAppState => {
  const updatedProductMappings = action.meta.updatedMappingsArray;

   if (isLoading) {
    return addLoading(state, KEY_ACTION, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(
      withBreaker,
      KEY_ACTION,
      KEY_ACTION,
      getResource(state.locale, 'PRODUCT_MAPPING_PAGE_SAVE_ERROR'),
    );
  }

  const noLoadingOrError = removeLoadingAndError(withBreaker, KEY_ACTION, KEY_ACTION);

  if (updatedProductMappings === undefined || updatedProductMappings.length === 0) {
    return noLoadingOrError;
  }

  // So a Mapping Status response requested before this cannot put the old values back
  const saved = assoc('productMappingsSavedAt', Date.now(), noLoadingOrError);

  // Update state since product mapping saved without error
  const isRTP = state.resortMappingHistories.posProvider === 'RTP';
  const hasError = state.resortMappingHistories.hasError;
  if (isRTP) {
    const rtpMappings = state.resortMappingHistories.productMappings as RTPMappingProduct[];
    const rtpUpdates = updatedProductMappings as RTPMappingProduct[];
    const mergedMappings = mergeRTPMappings(rtpUpdates, rtpMappings);

    return assoc(
      'resortMappingHistories', 
      {
        posProvider: 'RTP',
        productMappings: mergedMappings,
        hasError: hasError
      }, 
      saved);
  }
  else {
    const swMappings = state.resortMappingHistories.productMappings as SiriuswareMappingProduct[];
    const swUpdates = updatedProductMappings as SiriuswareMappingProduct[];
    const mergedMappings = mergeSWMappings(swUpdates, swMappings);

    return assoc(
      'resortMappingHistories', 
      {
        posProvider: 'Siriusware',
        productMappings: mergedMappings,
        hasError: hasError
      }, 
      saved);
  }
};

const mergeRTPMappings = (rtpUpdates: RTPMappingProduct[], existingMappings: RTPMappingProduct[]) => {
    const mappings = existingMappings.map((mapping) => {
      const foundUpdate = rtpUpdates.find((item) => item.id === mapping.id);
      if (foundUpdate) {
        return foundUpdate;
      }
      return mapping;
    });
    return mappings;
  };

const mergeSWMappings = (swUpdates: SiriuswareMappingProduct[], existingMappings: SiriuswareMappingProduct[]) => {
  const mappings = existingMappings.map((mapping) => {
    const foundUpdate = swUpdates.find((item) => item.id === mapping.id);
    if (foundUpdate) {
      return foundUpdate;
    }
    return mapping;
  });
  return mappings;
};
 
  


/**
 * Function that maps actions into new app state. Specifically, updates app state with information from actions related
 * to a guest's redemptions at resorts while using a specific pass. Returns a new app state, of course.
 */
export const updateResortMappingReducer = makeApiReducer<ResortMappingObject>(ACTIONS, KEY_ACTION, reducer);
